import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Checkbox,
  DialogContentText,
  FormControlLabel,
  InputAdornment,
  Snackbar,
  TextField,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import SearchIcon from "@mui/icons-material/Search";

import AppButton from "../ui/AppButton";
import PageHeader from "../ui/PageHeader";
import DeleteDialog from "../modals/DeleteDialog";
import DepartmentDialog from "../components/departments/DepartmentDialog";
import DepartmentTable from "../components/departments/DepartmentTable";

import {
  createDepartment,
  deleteDepartment,
  updateDepartment,
} from "../services/departmentService";
import { useDepartments } from "../context/DepartmentsContext";

function Departments() {
  const { departments: allDepartments, loading, refreshDepartments } = useDepartments();
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);

  const [search, setSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [deleteRelated, setDeleteRelated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const departments = useMemo(
    () => allDepartments.filter((department) => (
      department.department_name.toLowerCase().includes(search.toLowerCase())
    )),
    [allDepartments, search]
  );

  useEffect(() => {
    // Keep only valid selected ids after the shared list/search changes.
    setSelectedDepartmentIds((prev) => {
      const validIds = new Set(departments.map((department) => department.department_id));
      return prev.filter((id) => validIds.has(id));
    });
  }, [departments]);

  const handleSave = async (data) => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      if (selectedDepartment) {
        await updateDepartment(selectedDepartment.department_id, data);
      } else {
        await createDepartment(data);
      }

      setOpen(false);
      setSelectedDepartment(null);

      await refreshDepartments();

      setSnackbar({
        open: true,
        message: "Department saved successfully",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Operation failed",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setSelectedDepartment(null);
    setDeleteRelated(false);
  };

  const handleDelete = async () => {
    if (!selectedDepartment || isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);

      await deleteDepartment(selectedDepartment.department_id, deleteRelated);
      closeDeleteDialog();

      await refreshDepartments();

      setSnackbar({
        open: true,
        message: deleteRelated
          ? "Department and related records deleted successfully"
          : "Department deleted successfully",
        severity: "success",
      });
    } catch (error) {
      closeDeleteDialog();
      console.error(error);

      const message = error?.response?.data?.detail || "Delete failed";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDepartmentIds.length === 0 || isBulkDeleting) {
      return;
    }

    try {
      setIsBulkDeleting(true);

      let successCount = 0;
      let failedCount = 0;

      for (const departmentId of selectedDepartmentIds) {
        try {
          await deleteDepartment(departmentId, true);
          successCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      setBulkDeleteOpen(false);
      await refreshDepartments();
      setSelectedDepartmentIds([]);

      if (failedCount === 0) {
        setSnackbar({
          open: true,
          message: `${successCount} department(s) deleted successfully`,
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: `${successCount} deleted, ${failedCount} failed`,
          severity: "warning",
        });
      }
    } catch (error) {
      console.error(error);
      setBulkDeleteOpen(false);
      setSnackbar({
        open: true,
        message: "Bulk delete failed",
        severity: "error",
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: 3,
      }}
    >
      <PageHeader
        title="Departments"
        subtitle="Manage and organize all departments in the organization."
        actions={(
          <>
            <AppButton
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              loading={isBulkDeleting}
              onClick={() => setBulkDeleteOpen(true)}
              disabled={selectedDepartmentIds.length === 0}
            >
              {isBulkDeleting ? "Deleting..." : `Delete All (${selectedDepartmentIds.length})`}
            </AppButton>

            <AppButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedDepartment(null);
                setOpen(true);
              }}
            >
              Add Department
            </AppButton>
          </>
        )}
      />

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 1.5,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search department name..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <DepartmentTable
        departments={departments}
        loading={loading}
        selectedIds={selectedDepartmentIds}
        onSelectionChange={setSelectedDepartmentIds}
        onEdit={(department) => {
          setSelectedDepartment(department);
          setOpen(true);
        }}
        onDelete={(department) => {
          setSelectedDepartment(department);
          setDeleteRelated(false);
          setDeleteOpen(true);
        }}
      />

      <DepartmentDialog
        open={open}
        department={selectedDepartment}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) {
            return;
          }
          setOpen(false);
          setSelectedDepartment(null);
        }}
        onSave={handleSave}
      />

      <DeleteDialog
        open={deleteOpen}
        isDeleting={isDeleting}
        title="Delete Department"
        message="Are you sure you want to delete this department?"
        itemName={selectedDepartment?.department_name}
        extra={(
          <FormControlLabel
            sx={{ mt: 1 }}
            control={(
              <Checkbox
                checked={deleteRelated}
                onChange={(event) => setDeleteRelated(event.target.checked)}
                disabled={isDeleting}
              />
            )}
            label="Delete all related records"
          />
        )}
        onClose={() => {
          if (isDeleting) {
            return;
          }
          closeDeleteDialog();
        }}
        onConfirm={handleDelete}
      />

      <DeleteDialog
        open={bulkDeleteOpen}
        onClose={() => {
          if (isBulkDeleting) {
            return;
          }
          setBulkDeleteOpen(false);
        }}
        onConfirm={handleBulkDelete}
        isDeleting={isBulkDeleting}
        title="Delete Selected Departments"
        message="Are you sure you want to delete all selected departments?"
        itemName={`Selected: ${selectedDepartmentIds.length}`}
        extra={(
          <DialogContentText sx={{ mt: 1 }}>
            This will remove related records for each selected department.
          </DialogContentText>
        )}
        confirmLabel="Delete All"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Departments;
